import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { OmniCampaign } from '@feathr/blackbox';
import { Alert, Button, CardV2 as Card, Slider, Text, Tooltip } from '@feathr/components';
import type { IAudienceBreakdownReturn } from '@feathr/report_components';

import BudgetBreakdown from './BudgetBreakdown';
import { useChannelBudgetSplit } from './ChannelBudgetSplit.hooks';
import { MAX_SPLIT, MIN_SPLIT } from './ChannelBudgetSplit.utils';

import * as styles from './ChannelBudgetSplit.css';

const SLIDER_MARKS = [
  { value: MIN_SPLIT, label: `${MIN_SPLIT}%` },
  { value: 50, label: '50%' },
  { value: MAX_SPLIT, label: `${MAX_SPLIT}%` },
];

function formatTickLabel(value: number): string {
  return `${value}%`;
}

interface IChannelBudgetSplitProps {
  campaign: OmniCampaign;
  audienceData: IAudienceBreakdownReturn;
}

function ChannelBudgetSplit({
  campaign,
  audienceData,
}: Readonly<IChannelBudgetSplitProps>): JSX.Element {
  const { t } = useTranslation();

  const {
    displayPercent,
    setDisplayPercent,
    displayBudget,
    metaBudget,
    showAudienceDriftWarning,
    handleSliderChangeEnd,
    handleUseRecommended,
  } = useChannelBudgetSplit({ campaign, audienceData });

  const tooltipContent = t(
    'Adjust how your budget is divided between Display and Meta advertising channels. The default split is based on the relative audience sizes for each channel.',
  );

  const titleSuffix = (
    <Tooltip title={tooltipContent}>
      <FontAwesomeIcon icon={faCircleInfo} />
    </Tooltip>
  );

  const description = t('Control how your budget is distributed across Meta and Display channels.');

  return (
    <Card width={'narrow'}>
      <Card.Header
        description={description}
        title={t('Channel Budget Split')}
        titleSuffix={titleSuffix}
      />
      <Card.Content addVerticalGap={true}>
        {showAudienceDriftWarning && (
          <Alert
            description={t(
              'Your audience selection has changed. Please review your budget split to ensure it still aligns with your campaign goals.',
            )}
            name={'audience_drift_warning'}
            title={t('Audience has changed')}
            type={'warning'}
          />
        )}

        <Flex className={styles.sliderLabels}>
          <Text.SmallMedium>{t('Display')}</Text.SmallMedium>
          <Text.SmallMedium>{t('Meta')}</Text.SmallMedium>
        </Flex>
        <Slider
          dataName={'channel_split_slider'}
          labelAlwaysOn={true}
          marks={SLIDER_MARKS}
          max={MAX_SPLIT}
          min={MIN_SPLIT}
          onChange={setDisplayPercent}
          onChangeEnd={handleSliderChangeEnd}
          step={1}
          tickLabel={formatTickLabel}
          value={displayPercent}
        />

        <BudgetBreakdown
          displayBudget={displayBudget}
          displayPercent={displayPercent}
          metaBudget={metaBudget}
        />

        <Button name={'use_recommended_split'} onClick={handleUseRecommended} type={'secondary'}>
          {t('Use recommended split')}
        </Button>
      </Card.Content>
    </Card>
  );
}

export default observer(ChannelBudgetSplit);
